import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { LanguageToggle } from "../components/LanguageToggle";
import { LegalFooterLinks } from "../components/LegalFooterLinks";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login.welcome"));
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error(t("login.tooManyAttempts"));
      else if (status === 403 && typeof err?.response?.data?.detail === "string") toast.error(err.response.data.detail, { duration: 10000 });
      else if (status && status !== 401) toast.error(t("login.serverError"));
      else toast.error(t("login.badCredentials"));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBF9F5]">
      {/* Visuel */}
      <div className="relative hidden lg:block overflow-hidden bg-[#0f172a]">
        <img
          src="https://images.unsplash.com/photo-1741850419928-aa65b3d5e3a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Villa méditerranéenne avec piscine"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-75"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/50 to-[#0f172a]/5" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/casaneo-wordmark.jpg" alt="Casanéo" className="h-16 w-auto rounded-xl object-contain shadow-lg shadow-black/30" />
          </div>
          <div>
            <h1 className="font-serif text-5xl leading-tight mb-4">
              {t("login.heroLine1")}<br />
              <span className="text-brass">{t("login.heroLine2")}</span>
            </h1>
            <p className="text-slate-300 max-w-md">{t("login.heroBody")}</p>
          </div>
          <p className="text-xs text-slate-400 font-mono">{t("login.heroTag")}</p>
        </div>
      </div>

      {/* Formulaire */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/casaneo-wordmark.jpg" alt="Casanéo" className="h-12 w-auto rounded-lg object-contain" />
          </div>

          <div className="flex items-start justify-between gap-3 mb-2">
            <h2 className="font-serif text-4xl">{t("login.title")}</h2>
            <LanguageToggle />
          </div>
          <p className="text-sm text-muted-foreground mb-8">{t("login.subtitle")}</p>

          <Button
            type="button"
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="w-full h-11 gap-3 border-border bg-white hover:bg-secondary"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            {t("login.google")}
          </Button>

          <div className="flex items-center gap-4 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground font-mono uppercase">{t("login.or")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-medium">{t("login.email")}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@conciergerie.fr"
                  className="pl-10 h-11 bg-white"
                  required
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-medium">{t("login.password")}</Label>
                <Link to="/mot-de-passe-oublie" className="text-xs text-brass hover:underline" data-testid="login-forgot-password-link">
                  {t("login.forgot")}
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-11 bg-white"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-[#0f172a] hover:bg-[#1e293b] gap-2 group"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.submit")}
              {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            {t("login.mobileNote")}
          </p>
          <p className="mt-3 text-center text-[11px] text-muted-foreground/70">
            {t("login.displayIssue")}{" "}
            <a href="/reset" className="underline hover:text-foreground" data-testid="login-reset-link">
              {t("login.resetApp")}
            </a>
          </p>
          <LegalFooterLinks className="mt-6" />
        </motion.div>
      </div>
    </div>
  );
}
